/**
 * Proxy Contract — Difficulty Scale (FG1: Cito-waarde Paradox)
 *
 * ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §F0.A + §FG1.
 *
 * THE PROBLEM
 * ===========
 * Seeds store `citoDifficulty` in the range 0.15–0.75 with the IRT-style
 * convention `higher = harder`. The NAME suggests a Cito p-value (proportion
 * of children correct → `higher = easier`), which is the OPPOSITE direction.
 *
 * Today there is NO math bug: the ELO engine never reads `citoDifficulty`,
 * and `assessmentBlueprintGenerator` sorts ascending (correct for
 * higher=harder). But the moment an engineer "logically" wires a real 2-PL
 * coupling `P(correct) = σ(θ − b)` to the field, the name introduces a
 * silent inversion that scales children systematically too easy.
 *
 * THE CONTRACT
 * ============
 * This module is the SINGLE conversion boundary for difficulty semantics:
 *   - `IRTDifficulty`  — branded, normalised difficulty index. higher = harder.
 *   - `CitoPValue`     — branded, proportion-correct. higher = easier.
 * The brands make a wrong coupling a COMPILE-TIME error instead of a silent
 * didactic drift. Raw `number`s never cross a module boundary as difficulty.
 *
 * NOTE: The seed value is a *normalised* difficulty index, NOT a true IRT
 * b-parameter (which ranges ~−3..+3) and NOT a true p-value. The Cito
 * conversion below is a documented, MONOTONE approximation for display /
 * stakeholder communication only — it is NOT psychometric calibration.
 * Empirical calibration remains the long-term north-star (FG1 §6, FG5 §4).
 */

/** Normalised difficulty index. Convention: higher = harder. Range [0, 1]. */
export type IRTDifficulty = number & {
  readonly __brand: 'IRTDifficulty';
};

/** Cito-style p-value (proportion correct). Convention: higher = easier. Range [0, 1]. */
export type CitoPValue = number & {
  readonly __brand: 'CitoPValue';
};

/** Canonical valid range for the normalised difficulty index in seeds. */
export const DIFFICULTY_INDEX_MIN = 0.1;
export const DIFFICULTY_INDEX_MAX = 0.85;
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Convert a raw seed difficulty (`citoDifficulty`) into the branded
 * `IRTDifficulty`. Non-finite input falls back to the midpoint (0.5),
 * which is a deliberate, neutral fallback semantic.
 */
export function toIRTDifficulty(raw: number | undefined | null): IRTDifficulty {
  if (raw == null || !Number.isFinite(raw)) {
    return 0.5 as IRTDifficulty;
  }
  return clamp(raw, 0, 1) as IRTDifficulty;
}

/**
 * Monotone, DISPLAY-ONLY conversion from the normalised difficulty index to a
 * Cito-style p-value. Because difficulty (higher=harder) is the inverse of
 * a p-value (higher=easier), the mapping is `p ≈ 1 − difficultyIndex`.
 *
 * WARNING: this is an approximation for human-facing labels, not a calibrated
 * psychometric transform. Do NOT feed this into an ELO / IRT computation.
 */
export function irtDifficultyToCitoPValue(d: IRTDifficulty): CitoPValue {
  return clamp(1 - d, 0, 1) as CitoPValue;
}

/**
 * Inverse of the above — given a Cito p-value, recover the normalised
 * difficulty index. Same approximation caveat applies.
 */
export function citoPValueToIRTDifficulty(p: CitoPValue): IRTDifficulty {
  return clamp(1 - p, 0, 1) as IRTDifficulty;
}

/**
 * Per-module difficulty-curve offsets (P2 calibration, mei 2026;
 * extended juni 2026 — non-procedural cognitive profiles).
 *
 * The generic band curve (0.15→0.75) is calibrated to PROCEDURAL-MATH
 * difficulty escalation — the same pedagogy Webb's DOK was built for.
 * Non-procedural modules measurably run LOWER on the normalised
 * difficulty index at the same tier, because their failure mode at the
 * upper tiers is NOT arithmetic accuracy:
 *
 *  - reading-comprehension (Taalbegrip): inference depth, flatter slope.
 *    LEZEN items at tier 13-15 cluster at 0.30-0.45, not 0.55-0.70.
 *  - study-skill / comprehension (Studievaardig): strategy recall +
 *    interpretation — STU-ROUTE/KAART/MINDMAP/SAMENVATTEN seeds cluster
 *    0.20-0.35 at tier 9-13 where the generic center sits 0.47-0.63.
 *  - fact recall (Topo, SpellingRegel loanword rules): recall stays
 *    recall even on E8+ — HOOFDST/KLIMAAT/LEEN seeds cluster 0.25-0.35.
 *
 * This mirrors the cognitive-profile reasoning that already governs DOK
 * floors in `tierBands.ts` (procedural-math vs comprehension vs
 * recall-knowledge): the SAME modules that legitimately cap DOK lower
 * also legitimately sit lower on the difficulty curve. Without this the
 * schema-guard emits ~15 false SCHEMA_DIFFICULTY_TIER_INVERSION issues on
 * legitimate Studievaardig / Topo / SpellingRegel seeds (see governance
 * validation report 14 juni 2026).
 *
 * IMPORTANT — kept INTENTIONALLY NARROW: procedural-math modules
 * (Basisrekenen, Getalbegrip, Metriek, Tijd, Geld) get NO offset, so a
 * genuinely too-easy plus-question (e.g. GET-WETENSCHAP tier 16 @ 0.38)
 * or a too-hard low-tier seed stays flagged. The offset only relaxes the
 * curve where the module's authentic difficulty profile is non-procedural.
 *
 * The offset is applied to the band CENTER only on tiers >= 9 — lower
 * tiers fall within the default tolerance anyway. Tolerance width stays
 * the same so the guard still catches gross inversions.
 *
 * Keys MUST match the module display strings used in
 * tierBands.MODULE_COGNITIVE_PROFILES.
 */
const MODULE_DIFFICULTY_CENTER_OFFSET: Readonly<Record<string, number>> = Object.freeze({
  Taalbegrip: -0.15,
  // application-language / reading comprehension
  Studievaardig: -0.2,
  // comprehension / study-skill curve sits lower
  Topo: -0.2,
  // recall-knowledge — facts stay easy on the index
  SpellingRegel: -0.15 // recall spelling rules; conservative vs Topo
});
const QUESTION_TYPE_DIFFICULTY_OFFSET: Readonly<Record<string, number>> = Object.freeze({
  map_locate_point: 0.15,
  // Free-placement is harder than snap-to-dot/MC
  map_place_cities: 0.25,
  // Multi-placement is much harder
  comparison_judge: 0.15 // Judging is harder than simple calculation
});

/**
 * Family-pattern difficulty-curve offsets (juni 2026 — sub-module cognitive
 * profile).
 *
 * Some families live inside a PROCEDURAL-MATH module (which gets no
 * MODULE_DIFFICULTY_CENTER_OFFSET by design) yet have an authentic difficulty
 * profile that is NOT procedural-arithmetic. The clearest case is Metriek
 * GEOMETRY: cirkel-begrippen (diameter↔straal), omtrek, oppervlakte en
 * hoek-berekeningen zijn op groep-8-niveau grotendeels CONCEPT-RECALL +
 * één-staps-procedure. Ze discrimineren sterk (≈0.74-0.78) maar zitten
 * legitiem laag op de difficulty-index (0.30-0.48 op tier 13-16) — precies
 * zoals een schone, makkelijke recall-item hoort.
 *
 * Prior didactic verdicts confirm this is real, not seed-error:
 *   - GROUP8_DIFFICULTY_AUDIT_2026 §"MET-*": "π-berekeningen geven inherent
 *     niet-ronde uitkomsten; concept-vragen waar moeilijkheid in conceptueel
 *     begrip zit."
 *   - GOVERNANCE_REVIEW_TRIAGE_2026 B6.1: de tier-band-lowerbound vangt deze
 *     items 0.02-0.08 te streng; seeds NIET tamperen.
 *
 * CRITICAL — kept INTENTIONALLY NARROW (anti-bandaid principe #3):
 * the offset is scoped to the Metriek GEOMETRY family prefixes ONLY. The rest
 * of Metriek (metric-conversion arithmetic: LEN/GEW/INH-OMREKEN) keeps the
 * full procedural curve, and Getalbegrip is untouched — so a genuinely
 * too-easy plus-question like GET-WETENSCHAPPELIJK-001 (recall @ tier 16,
 * 0.42) STAYS flagged (it is also CONTENT_PLUS_INADEQUATE — a real didactic
 * gap, not a band false-positive). The offset only relaxes the band CENTER on
 * tier >= 9; tolerance width is unchanged so gross inversions still fire.
 *
 * Keys are matched as a prefix against `familyId`.
 */
const FAMILY_PATTERN_DIFFICULTY_OFFSET: ReadonlyArray<readonly [prefix: string, offset: number]> = Object.freeze([
// Metriek geometry — concept-recall + single-step procedure, not the
// procedural-arithmetic escalation the generic curve assumes.
['MET-MEETK-', -0.12],
// cirkels: diameter/straal, omtrek, oppervlakte, deel
['MET-HOEK-BEREKENEN', -0.12],
// hoeksom-regels: 180°/360° toepassen
['MET-SCHAAL-OPP', -0.1] // kwadrateren via trap — conceptueel, één extra stap
]);
function familyDifficultyOffset(familyId: string | undefined): number {
  if (!familyId) return 0;
  for (const [prefix, offset] of FAMILY_PATTERN_DIFFICULTY_OFFSET) {
    if (familyId.startsWith(prefix)) return offset;
  }
  return 0;
}

/**
 * Expected difficulty band for a tier (FG1 §4 — schema-guard helper).
 *
 * Difficulty should rise MONOTONICALLY with tier (1..16). This returns the
 * expected `[min, max]` difficulty-index window for a given tier so the
 * governance schema stage can flag inverted seeds (a low-tier question
 * tagged hard, or a high-tier question tagged trivial) BEFORE production.
 *
 * The band is intentionally generous (wide window, gentle slope) so it only
 * catches gross inversions, not normal within-tier variation.
 *
 * P2 CALIBRATION (mei 2026): pass `module` to apply per-module curve
 * offsets — see MODULE_DIFFICULTY_CENTER_OFFSET. The offset only kicks
 * in on tier >= 9 where the curves measurably diverge.
 */
export function expectedDifficultyBandForTier(tier: number, module?: string, questionType?: string, familyId?: string): {
  min: IRTDifficulty;
  max: IRTDifficulty;
} {
  const t = clamp(tier, 1, 16);
  // Linear ramp from ~0.15 (tier 1) to ~0.75 (tier 16), ±0.25 tolerance.
  let center = 0.15 + (t - 1) / 15 * (0.75 - 0.15);

  // P2: apply module-specific offset on advanced/expert tiers only.
  if (module && t >= 9) {
    const offset = MODULE_DIFFICULTY_CENTER_OFFSET[module];
    if (offset !== undefined) center += offset;
  }

  // Family-pattern offset (sub-module cognitive profile) — also tier >= 9 only,
  // so low-tier basics are untouched. See FAMILY_PATTERN_DIFFICULTY_OFFSET.
  if (familyId && t >= 9) {
    center += familyDifficultyOffset(familyId);
  }
  if (questionType) {
    const typeOffset = QUESTION_TYPE_DIFFICULTY_OFFSET[questionType];
    if (typeOffset !== undefined) center += typeOffset;
  }
  return {
    min: clamp(center - 0.25, 0, 1) as IRTDifficulty,
    max: clamp(center + 0.25, 0, 1) as IRTDifficulty
  };
}

/**
 * Returns true when the difficulty index sits within the expected band for
 * its tier. Used by the FG1 schema-guard to detect inverted seeds.
 *
 * P2 CALIBRATION: pass `module` so per-module curve offsets are honoured.
 */
export function isDifficultyConsistentWithTier(difficulty: IRTDifficulty, tier: number, module?: string, questionType?: string, familyId?: string): boolean {
  const band = expectedDifficultyBandForTier(tier, module, questionType, familyId);
  return difficulty >= band.min && difficulty <= band.max;
}

/**
 * Minimal question shape needed to read difficulty. Kept structural to avoid
 * importing the full QuestionCore type across the module boundary.
 */
export interface DifficultyBearing {
  citoDifficulty?: number;
  tier?: number;
}

/**
 * CANONICAL read path for a question's difficulty (FG1 §2).
 *
 * Consumers should call this instead of reading `question.citoDifficulty`
 * directly. The future `no-direct-citoDifficulty` lint-rule flags every raw
 * read outside `seedConsolidator` / `questionLoader`; this accessor is the
 * sanctioned conversion boundary. Returns a branded `IRTDifficulty`
 * (higher = harder) so a wrong coupling is a compile-time error.
 */
export function getQuestionDifficulty(q: DifficultyBearing): IRTDifficulty {
  return toIRTDifficulty(q.citoDifficulty);
}